import styled from '@emotion/styled'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'

const brandGradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%)'

const FieldWrap = styled.div`
  width: 100%;
`

const StyledDateTimePicker = styled(DateTimePicker)`
  width: 100%;

  .MuiInputBase-root {
    background-color: #fff;
    border-radius: 14px;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }

  .MuiOutlinedInput-input {
    cursor: pointer;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(23, 23, 23, 0.1) !important;
    border-radius: 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(201, 77, 170, 0.4) !important;
  }

  .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(201, 77, 170, 0.55) !important;
    border-width: 1px !important;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }

  .MuiInputAdornment-root .MuiSvgIcon-root {
    color: #c94daa;
  }
`

interface DatePickerProps {
  value: string
  onChangeData: (e: string) => void
  label: string
}

const DatePicker: React.FC<DatePickerProps> = ({
  onChangeData,
  value,
  label,
}) => {
  const [open, setOpen] = useState(false)
  const fieldRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    // Capture phase so parent modal stopPropagation can't block closing
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target) return

      const inField = fieldRef.current?.contains(target)
      const inPopper = (target as Element).closest?.(
        '.MuiPickersPopper-root, .MuiDialog-root, .MuiModal-root'
      )

      if (!inField && !inPopper) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown, true)
    document.addEventListener('touchstart', handlePointerDown, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true)
      document.removeEventListener('touchstart', handlePointerDown, true)
    }
  }, [open])

  return (
    <FieldWrap ref={fieldRef}>
      <StyledDateTimePicker
        label={label || undefined}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        minDate={dayjs().startOf('day')}
        onChange={(e) => {
          if (e) onChangeData(e.toDate().toString())
        }}
        value={dayjs(value)}
        format="DD/MM/YYYY HH:mm"
        orientation="landscape"
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            onClick: () => setOpen(true),
            inputProps: {
              readOnly: true,
            },
            sx: {
              '& .MuiOutlinedInput-root': {
                height: 48,
                cursor: 'pointer',
              },
              '& .MuiOutlinedInput-input': {
                padding: '12px 14px',
                height: 48,
                boxSizing: 'border-box',
                cursor: 'pointer',
              },
            },
          },
          openPickerButton: {
            onClick: (e) => {
              e.stopPropagation()
              setOpen(true)
            },
          },
          day: {
            sx: {
              borderRadius: '12px',
              fontWeight: 600,
              ':not(.Mui-selected):hover': {
                backgroundColor: 'rgba(201, 77, 170, 0.12)',
                color: '#c94daa',
              },
              '&.MuiPickersDay-root.Mui-selected': {
                color: '#fff',
                background: brandGradient,
                boxShadow: '0 4px 12px rgba(201, 77, 170, 0.28)',
                ':hover': {
                  color: '#fff',
                  background: brandGradient,
                },
              },
            },
          },
          yearButton: {
            sx: {
              borderRadius: '12px',
              fontWeight: 600,
              '&:hover': { background: 'rgba(201, 77, 170, 0.12)', color: '#c94daa' },
              '&.Mui-selected': {
                background: brandGradient,
                color: '#fff',
              },
            },
          },
          monthButton: {
            sx: {
              borderRadius: '12px',
              fontWeight: 600,
              '&:hover': { background: 'rgba(201, 77, 170, 0.12)', color: '#c94daa' },
              '&.Mui-selected': {
                background: brandGradient,
                color: '#fff',
              },
            },
          },
          popper: {
            sx: {
              zIndex: 14000,
              '& .MuiPaper-root': {
                borderRadius: '18px',
                border: '1px solid rgba(23, 23, 23, 0.06)',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.16)',
                overflow: 'hidden',
              },
            },
          },
        }}
      />
    </FieldWrap>
  )
}

export default DatePicker
